import React, { useEffect, useReducer, useRef, useState } from "react";
import Select from "react-select";
import { Rate } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import { getCountries } from "../../../services/CountryService";
import { createJob, getJobById, updateJob } from "../../../services/JobService";
import Swal from "sweetalert2";
import { Button } from "react-bootstrap";

const AddJob = () => {
  const [formData, setFormData] = useState({
    company_email: "",
    company_mobile: "",
    job_title: "",
    job_desc: "",
    job_location: "",
    job_website: "",  
    country_id: null,
  });

  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [countryOptions, setCountryOptions] = useState([]);
  const [countryPage, setCountryPage] = useState(1);
  const [countryTotalPages, setCountryTotalPages] = useState(1);
  const [countryLoading, setCountryLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const hasFetched = useRef(false);

  const fetchCountry = async (pageNumber = 1, query = "") => {
    if (pageNumber > countryTotalPages) return; // no more pages
    try {
      setCountryLoading(true);
      const limit = 5; // set per-page limit
      const response = await getCountries(pageNumber, limit, query);
      const { data, pagination } = response.data;

      const options = (data || []).map((c) => ({ value: c.id, label: c.name }));

      if (pageNumber === 1) {
        // first page or search => replace
        setCountryOptions(options);
      } else {
        // append for scroll
        setCountryOptions((prev) => [...prev, ...options]);
      }

      setCountryPage(pagination?.page || pageNumber);
      setCountryTotalPages(pagination?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching countries:", error);
    } finally {
      setCountryLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.job_title.trim())
      newErrors.job_title = "Job Title is required";
    if (!formData.job_website.trim())
      newErrors.job_website = "Job Website is required";
    if (!formData.job_location.trim())
      newErrors.job_location = "Job Description is required";
    if (!formData.country_id) newErrors.country_id = "Country is required";

    return newErrors;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        // 1️⃣ Fetch dropdowns first
        const [countryRes] = await Promise.all([getCountries(1, 5000)]);

        const countryOpts = (countryRes.data?.data || []).map((c) => ({
          value: c.id,
          label: c.name,
        }));
        setCountryOptions(countryOpts);

        if (id) {
          const res = await getJobById(id);
          if (res.data?.success) {
            const job = res.data.data;
            setFormData({
              company_email: job.company_email || "",
              company_mobile: job.company_mobile || "",
              job_title: job.job_title || "",
              job_desc: job.job_desc || "",
              job_location: job.job_location || "",
              job_website: job.job_website || "",
              country_id:
                countryOpts.find((c) => c.value === job.country_id) || null,
            });
          } else {
            console.error("Failed to fetch job by ID");
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchData();
    }
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const fd = new FormData();
      // append normal fields
      const submitData = {
        ...formData,
        country_id: formData.country_id?.value || null, // ✅ only ID
      };

      if (id) {
        const response = await updateJob(id, submitData);
        if (response.data?.success) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: response.data?.message || "Job details updated successfully!",
          });
          navigate("/job", {
            state: {
              page: location.state?.page || 1,
              searchTerm: location.state?.searchTerm || "",
            },
          });
        } else {
          console.error(
            "Error updating job:",
            response?.data?.message || "Unknown error"
          );
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response?.data?.message || "Failed to update job",
          });
        }
      } else {
        const response = await createJob(submitData);
        if (response.data?.success) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: response.data?.message || "Job details added successfully",
          });
          navigate("/job", {
            state: {
              page: location.state?.page || 1,
              searchTerm: location.state?.searchTerm || "",
            },
          });
        } else {
          console.error(
            "Error updating job:",
            response.data?.message || "Unknown error"
          );
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data?.message || "Failed to added job",
          });
        }
      }
    } catch (err) {
      console.error(
        "Error while adding or updating job:",
        err || "Unknown error"
      );
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err || "Failed to adding or updating job",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div id="preloader">
        <div className="sk-three-bounce">
          <div className="sk-child sk-bounce1"></div>
          <div className="sk-child sk-bounce2"></div>
          <div className="sk-child sk-bounce3"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageTitle
        activeMenu={`${id ? "Edit Job" : "Add Job"}`}
        motherMenu="Job"
        activeMenuLink={`${id ? `edit-job/${id}` : "add-job"}`}
        motherMenuLink={"job"}
      />
      <div className="row">
        <div className="col-xl-12">
          <div className="row">
            <div className="col-xl-12 col-xxl-12">
              <div className="card h-auto">
                <div className="card-body">
                  <form onSubmit={handleSubmit} encType="multipart/form-data">
                    <div
                      onClick={() => {
                        navigate("/job", {
                          state: {
                            page: location.state?.page || 1,
                            searchTerm: location.state?.searchTerm || "",
                          },
                        });
                      }}
                      className="mb-4 d-flex align-items-center flex-wrap gap-2 cursor-pointer w-max"
                    >
                      <i className="fas fa-chevron-left"></i>
                      <div>Job List</div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Company Name</label>
                      <input
                        type="text"
                        name="job_title"
                        className={`form-control ${errors.job_title ? "is-invalid" : ""
                          }`}
                        value={formData.job_title}
                        onChange={handleChange}
                        placeholder="Enter company name"
                      />
                      {errors.job_title && (
                        <div className="invalid-feedback">
                          {errors.job_title}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Company Email</label>
                      <input
                        type="text"
                        name="company_email"
                        className={`form-control ${errors.company_email ? "is-invalid" : ""
                          }`}
                        value={formData.company_email}
                        onChange={handleChange}
                        placeholder="Enter company name"
                      />
                      {errors.company_email && (
                        <div className="invalid-feedback">
                          {errors.company_email}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Company Mobile</label>
                      <input
                        type="text"
                        name="company_mobile"
                        className={`form-control ${errors.company_mobile ? "is-invalid" : ""
                          }`}
                        value={formData.company_mobile}
                        onChange={handleChange}
                        placeholder="Enter company name"
                      />
                      {errors.company_mobile && (
                        <div className="invalid-feedback">
                          {errors.company_mobile}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Job Description</label>
                      <textarea
                        name="job_desc"
                        className={`form-control ${errors.job_desc ? "is-invalid" : ""
                          }`}
                        value={formData.job_desc}
                        onChange={handleChange}
                        placeholder="Describe Job description..."
                        rows={3}
                      ></textarea>
                      {errors.job_desc && (
                        <div className="invalid-feedback">
                          {errors.job_desc}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Job Website</label>
                      <input
                        type="text"
                        name="job_website"
                        className={`form-control ${errors.job_website ? "is-invalid" : ""
                          }`}
                        value={formData.job_website}
                        onChange={handleChange}
                        placeholder="Enter job website"
                      />
                      {errors.job_website && (
                        <div className="invalid-feedback">
                          {errors.job_website}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Job Location</label>
                      <input
                        type="text"
                        name="job_location"
                        className={`form-control ${errors.job_location ? "is-invalid" : ""
                          }`}
                        value={formData.job_location}
                        onChange={handleChange}
                        placeholder="Enter job location"
                      />
                      {errors.job_location && (
                        <div className="invalid-feedback">
                          {errors.job_location}
                        </div>
                      )}
                    </div>

                    {/* Currency & Country */}
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Country</label>
                        <Select
                          value={formData.country_id}
                          onChange={(value) =>
                            handleSelectChange("country_id", value)
                          }
                          options={countryOptions}
                          placeholder="Select country"
                          closeMenuOnSelect={true}
                          menuPlacement="auto"
                          menuPosition="fixed"
                          onMenuScrollToBottom={() =>
                            fetchCountry(countryPage + 1)
                          } // load next page
                          // onInputChange={(inputValue) => fetchCountry(1, inputValue)} // search resets page
                          isLoading={countryLoading}
                        />
                        {errors.country_id && (
                          <div className="text-danger mt-1">
                            {errors.country_id}
                          </div>
                        )}
                      </div>
                    </div>

                    <hr style={{ margin: "0px" }} className="mx-2" />
                    <div className="card-footer border-0 text-end py-3 d-flex justify-content-end gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => navigate("/job", {
                          state: {
                            page: location.state?.page || 1,
                            searchTerm: location.state?.searchTerm || "",
                          },
                        })}
                      >
                        Back
                      </Button>

                      {/* Save / Update Button */}
                      <Button type="submit" className="btn btn-primary btn-sm">
                        {id ? "Update" : "Save"}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddJob;
